// Schreiben-Assistent (#586): Versandprotokoll (schreiben + schreiben_empfaenger) – Lauf anlegen, Läufe laden,
// abgebrochenen Lauf zum Fortsetzen wieder aufbauen.
import { supabase } from '../supabase/client';
import type { BriefOptionen } from '../epost/epost';
import { ladeSchreibenEmpfaenger } from './empfaenger';
import type { Lauf, Stand } from './versand';
import type { WiedervorlageWunsch } from './wiedervorlage';
import type { EmpfaengerArt, SchreibenAnlage, SchreibenEmpfaenger, Zustellweg } from './typen';

export interface LaufDaten {
  vorlageId: string | null; betreff: string; text: string; eingaben: Record<string, string>; anlagen: SchreibenAnlage[];
  optionen: BriefOptionen; begleittext: string;
  /** Etappe 2: tatsächlich verwendeter Briefkopf (null = eingebaut) und Absender (null = ohne) */
  briefkopfId: string | null; absenderId: string | null;
  /** Etappe 3: Schriftform (E-Mail/Portal nur vorab) */
  schriftform: boolean;
  /** Etappe 4: Wiedervorlage-Wunsch (null = keine) */
  wiedervorlage: WiedervorlageWunsch | null;
}

export async function ladeStandardKonto(): Promise<{ id: string; email_adresse: string }> {
  const { data, error } = await supabase.from('email_konten').select('id, email_adresse').eq('aktiv', true)
    .order('ist_standard', { ascending: false }).limit(1).maybeSingle();
  if (error || !data) throw new Error('Kein aktives E-Mail-Postfach gefunden.');
  return data as { id: string; email_adresse: string };
}

/** Lauf + eine Zeile je Empfänger anlegen; liefert den fertigen Lauf für fuehreAus. */
export async function legeLaufAn(d: LaufDaten, liste: { e: SchreibenEmpfaenger; wege: Zustellweg[] }[]): Promise<Lauf> {
  const { data: s, error } = await supabase.from('schreiben').insert({
    vorlage_id: d.vorlageId, betreff: d.betreff, text: d.text, eingaben: d.eingaben, anlagen: d.anlagen,
    optionen: { ...d.optionen, begleittext: d.begleittext, briefkopf_id: d.briefkopfId, absender_id: d.absenderId, schriftform: d.schriftform, wiedervorlage: d.wiedervorlage },
  }).select('id').single();
  if (error || !s) throw new Error(`Protokoll nicht angelegt: ${error?.message ?? ''}`);
  const { data: zeilen, error: e2 } = await supabase.from('schreiben_empfaenger').insert(liste.map(({ e, wege }) => ({
    schreiben_id: s.id, art: e.art, empfaenger_id: e.id, name: e.name, wege, werte: e.werte,
  }))).select('id, art, empfaenger_id');
  if (e2 || !zeilen) throw new Error(`Protokoll nicht angelegt: ${e2?.message ?? ''}`);
  const nach = new Map(zeilen.map((z) => [`${z.art}:${z.empfaenger_id}`, z.id as string]));
  return {
    schreibenId: s.id, betreff: d.betreff, text: d.text, eingaben: d.eingaben, anlagen: d.anlagen, optionen: d.optionen, begleittext: d.begleittext,
    briefkopfId: d.briefkopfId, absenderId: d.absenderId, schriftform: d.schriftform, wiedervorlage: d.wiedervorlage, wiedervorlageTicketId: null,
    empfaenger: liste.map(({ e, wege }) => ({ e, wege, zeileId: nach.get(`${e.art}:${e.id}`)! })),
  };
}

export interface LaufZeile { id: string; name: string | null; art: string; empfaenger_id: string | null; wege: Zustellweg[]; stand: Stand | 'laeuft'; fehler: string | null; epost_brief_id: string | null; email_id: string | null; ablage_datei_id: string | null }
export interface SchreibenLauf {
  id: string; betreff: string | null; created_at: string; empfaenger: (LaufZeile & { werte?: Record<string, string> | null })[];
  eingaben?: Record<string, string> | null;
  /** Etappe 4: angelegte Wiedervorlage */
  wiedervorlage?: { id: string; ticket_nummer: string } | null;
}

export async function ladeLaeufe(limit = 50): Promise<SchreibenLauf[]> {
  const zeilen = 'empfaenger:schreiben_empfaenger(id, name, art, empfaenger_id, wege, stand, fehler, epost_brief_id, email_id, ablage_datei_id, werte)';
  // Wiedervorlage erst mit der Migration Etappe 4 – sonst ohne
  for (const auswahl of [`id, betreff, eingaben, created_at, ${zeilen}, wiedervorlage:wiedervorlage_ticket_id(id, ticket_nummer)`, `id, betreff, eingaben, created_at, ${zeilen}`]) {
    const { data, error } = await supabase.from('schreiben').select(auswahl).order('created_at', { ascending: false }).limit(limit);
    if (!error) return (data ?? []) as unknown as SchreibenLauf[];
  }
  return []; // Tabelle noch nicht da (Migration)
}

/** Lauf mit allen Empfängern neu aufbauen (Daten frisch geladen) – fuehreAus überspringt die Gesendeten. */
export async function ladeLaufZumFortsetzen(id: string): Promise<Lauf> {
  // wiedervorlage_ticket_id erst mit der Migration Etappe 4
  let kopf = await supabase.from('schreiben').select('id, betreff, text, eingaben, anlagen, optionen, wiedervorlage_ticket_id').eq('id', id).single();
  if (kopf.error) kopf = await supabase.from('schreiben').select('id, betreff, text, eingaben, anlagen, optionen').eq('id', id).single() as typeof kopf;
  const s = kopf.data as ({ wiedervorlage_ticket_id?: string | null } & Record<string, any>) | null;
  if (kopf.error || !s) throw new Error('Schreiben nicht gefunden');
  // druck_gesendet kommt mit der Migration Etappe 3 – vorher ohne
  const basis = 'id, art, empfaenger_id, name, wege, stand, werte, email_gesendet, post_gesendet, portal_gesendet, ablage_datei_id, dokument_id';
  let r = await supabase.from('schreiben_empfaenger').select(`${basis}, druck_gesendet`).eq('schreiben_id', id).neq('stand', 'gesendet');
  if (r.error) r = await supabase.from('schreiben_empfaenger').select(basis).eq('schreiben_id', id).neq('stand', 'gesendet') as typeof r;
  const zeilen = r.data as ({ druck_gesendet?: boolean } & Record<string, any>)[] | null;
  const empfaenger: Lauf['empfaenger'] = [];
  for (const z of zeilen ?? []) {
    if (!z.empfaenger_id) continue;
    let e: SchreibenEmpfaenger;
    try { e = await ladeSchreibenEmpfaenger({ art: z.art as EmpfaengerArt, id: z.empfaenger_id, name: z.name ?? '', sub: '' }); }
    catch (err) {
      await supabase.from('schreiben_empfaenger').update({ stand: 'fehler', fehler: `Empfänger nicht ladbar: ${(err as Error).message}` }).eq('id', z.id);
      continue;
    }
    // Werte wie beim ersten Senden (inkl. in der Eingabeleiste ergänzter)
    e = { ...e, werte: { ...e.werte, ...((z.werte ?? {}) as Record<string, string>) } };
    // Nur den fehlenden Teil wiederholen – niemand bekommt etwas doppelt
    const fertig: Record<Zustellweg, boolean> = { email: !!z.email_gesendet, post: !!z.post_gesendet, portal: !!z.portal_gesendet, druck: !!z.druck_gesendet };
    const alle = (z.wege ?? []) as Zustellweg[];
    const wege = alle.filter((w) => !fertig[w]);
    if (!wege.length) continue;
    empfaenger.push({ e, wege, erledigt: alle.filter((w) => fertig[w]), zeileId: z.id, stand: z.stand as Stand, ohneAblage: !!z.ablage_datei_id, dokumentId: z.dokument_id ?? null });
  }
  // alles, was nicht zu den Post-Optionen gehört, herauslösen (sonst ginge es an die E-POST)
  const { begleittext = '', briefkopf_id = null, absender_id = null, schriftform = false, wiedervorlage = null, ...optionen } = (s.optionen ?? {}) as BriefOptionen & {
    begleittext?: string; briefkopf_id?: string | null; absender_id?: string | null; schriftform?: boolean; wiedervorlage?: WiedervorlageWunsch | null };
  return {
    schreibenId: s.id, betreff: s.betreff ?? '', text: s.text ?? '', eingaben: s.eingaben ?? {}, anlagen: s.anlagen ?? [], optionen, begleittext,
    briefkopfId: briefkopf_id, absenderId: absender_id, schriftform, wiedervorlage, wiedervorlageTicketId: s.wiedervorlage_ticket_id ?? null, empfaenger,
  };
}
